import { ref, computed } from 'vue';
import {
  fetchReviewQueue,
  rqMeta,
  rqIsAnomaly,
  rqIsOverdue,
  rqLevelText,
  rqDocName,
  rqOwnerName,
} from './reviewQueueCore';

/**
 * AdminPriority (v3 homepage) — the comp/admin "Needs you" priority stack: the
 * few most urgent submissions ranked breach → overdue → awaiting, presented as
 * full-width horizontal cards (icon · identity · status+context · CTA), the lead
 * card emphasised. Backed by `POST Report/reviewQueue` (already ordered anomaly →
 * overdue → recent); this composable drops items needing no action (approved)
 * and shapes each row into a card. `self` pattern; caller injects an axios-like
 * `http`. Navigation stays in the presentation.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => number}  [options.getLimit] cards before "View all" (default 3)
 * @param {() => ('all'|'mine')} [options.getScope]
 * @param {() => void} [options.onReady] called once after the first fetch
 */
const SERVER_LIMIT = 200;
const DONE_KEYS = new Set(['approved']);

// status_key → the priority-card icon chip: tone (.v3-pcard-ic--<tone>) + icon.
// Mirrors the prototype V3AdminPriority ICON/TONE maps (anomaly/approval/
// revision/chase) expressed over our canonical status keys.
const CARD = {
  breach: { tone: 'breach', icon: 'flag' },
  overdue: { tone: 'attn', icon: 'notification_important' },
  'pending-approval': { tone: 'go', icon: 'fact_check' },
  're-review': { tone: 'go', icon: 'rate_review' },
  'pending-review': { tone: 'go', icon: 'rate_review' },
};
const CARD_FALLBACK = { tone: 'go', icon: 'inbox' };
// rqMeta tone → solid status-pill class (.v3-spill2--<tone>).
const SPILL2 = { rose: 'rose', amber: 'warn', accent: 'accent', violet: 'violet', green: 'green', neutral: 'neutral' };

export function adminPriority(options) {
  const { http, getScope = () => 'all', getLimit = () => 3, onReady = () => {} } = options || {};
  // Cards shown (prototype `max`, default 3) — configurable via widget settings.
  const limit = () => getLimit() || 3;

  const self = { rows: ref([]), loading: ref(false), showAll: ref(false) };

  self.isLoading = computed(() => self.loading.value);
  self.actionable = computed(() => self.rows.value.filter((r) => !DONE_KEYS.has(r && r.status_key)));
  self.total = computed(() => self.actionable.value.length);
  // Fixed top-3 stack — never an expandable list.
  self.hasMore = computed(() => false);

  const contextLine = (r) => {
    if (rqIsAnomaly(r)) return rqLevelText(r); // "Recommended 9, levelled at 3"
    const m = rqMeta(r);
    return [m.label, rqOwnerName(r)].filter(Boolean).join(' · ');
  };

  const toCard = (r, i) => {
    const meta = rqMeta(r);
    const card = CARD[r && r.status_key] || CARD_FALLBACK;
    const owner = rqOwnerName(r);
    return {
      row: r,
      doc_id: r.doc_id,
      reference: r.reference,
      title: rqDocName(r),
      meta: [r.reference, owner].filter(Boolean).join(' · '),
      cardTone: card.tone,
      cardIcon: card.icon,
      // status pill (solid)
      pillLabel: meta.label,
      pillIcon: meta.icon,
      pillTone: SPILL2[meta.tone] || 'neutral',
      context: contextLine(r),
      action: meta.action,
      anomaly: rqIsAnomaly(r),
      overdue: rqIsOverdue(r),
      lead: i === 0,
    };
  };

  self.cards = computed(() => self.actionable.value.map(toCard));
  self.shown = computed(() => self.cards.value.slice(0, limit()));

  async function load() {
    self.loading.value = true;
    try {
      self.rows.value = await fetchReviewQueue(http, { scope: getScope() || 'all', limit: SERVER_LIMIT });
    } catch {
      self.rows.value = [];
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  Promise.resolve(load()).finally(() => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  });

  return self;
}
